import { useState } from 'react'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import Styles from './ScheduledPostCard.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Calendar2, CalendarEdit, CalendarTrash, Edit1, EyeOpen } from 'blaze-ui/icons'
import dayjs from 'dayjs'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { Button } from 'blaze-ui/Button'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ActionKeys, useHandleAction } from '../useSchedulingOptions'

interface Props {
  fileId: string
  scheduledPostId: number
  channel: DesignChannel
}

const label = (status?: string, postUrl?: string) => {
  switch (status) {
    case PostStatusPropertyValue.ReminderScheduled:
      return 'Reminder set to'
    case PostStatusPropertyValue.Posted:
      if (!postUrl) return 'Marked as posted on'
      return 'Posted on'
    case PostStatusPropertyValue.AutoPostScheduled:
    default:
      return 'Scheduled to be posted on'
  }
}

export const ScheduledPostCard = ({ fileId, scheduledPostId, channel }: Props) => {
  const [imageDidError, setImageDidError] = useState(false)
  const { data: scheduledPost } = useScheduledPost({ id: scheduledPostId })
  const handleAction = useHandleAction({ fileId, scheduledPostId, channel })
  const { status, postAt, updateStatus } = usePostProperties({
    id: fileId,
    type: AssigneeType.Design,
  })
  const { avatar, name } = scheduledPost?.external_integration
    ? extractProfileInfo(scheduledPost.external_integration)
    : { avatar: undefined, name: undefined }
  const imageSrc = imageDidError ? Silhouette : avatar || Silhouette
  const formattedDate = postAt ? dayjs(postAt).format('ddd, MMM D h:mma z') : 'Not scheduled'

  if (!scheduledPost) return null

  return (
    <div className={Styles.body}>
      <Text variant="label" color="var(--dark-60)">
        {label(status?.value, scheduledPost.post_url)}
      </Text>
      <div className={Styles.postDetail}>
        <Calendar2 />
        <Text variant="secondary" color="var(--dark-80)">
          {formattedDate}
        </Text>
      </div>
      {imageSrc && name && (
        <div className={Styles.postDetail}>
          <img src={imageSrc} alt={name} className={Styles.avatar} onError={() => setImageDidError(true)} />
          <Text variant="secondary" color="var(--dark-80)">
            {name}
          </Text>
        </div>
      )}
      {[PostStatusPropertyValue.AutoPostScheduled, PostStatusPropertyValue.ReminderScheduled].includes(
        status?.value,
      ) && (
        <div className={Styles.buttons}>
          <Button
            size="sm"
            variant="tertiary"
            frontIcon={CalendarEdit}
            onPress={() =>
              handleAction(
                status.value === PostStatusPropertyValue.AutoPostScheduled
                  ? ActionKeys.SchedulePost
                  : ActionKeys.SetReminder,
              )
            }
          >
            Change Date
          </Button>
          <Button
            size="sm"
            variant="tertiary"
            frontIcon={CalendarTrash}
            onPress={() => updateStatus(PostStatusPropertyValue.Draft)}
          >
            Unschedule
          </Button>
        </div>
      )}
      {status?.value === PostStatusPropertyValue.Posted && scheduledPost?.post_url && (
        <ButtonLink
          frontIcon={EyeOpen}
          fullWidth
          size="sm"
          variant="tertiary"
          target="_blank"
          to={scheduledPost.post_url}
        >
          See Post
        </ButtonLink>
      )}
      {status?.value === PostStatusPropertyValue.Posted && !scheduledPost?.post_url && (
        <Button
          size="sm"
          variant="tertiary"
          frontIcon={Edit1}
          onPress={() => updateStatus(PostStatusPropertyValue.Draft)}
        >
          Move to Draft
        </Button>
      )}
    </div>
  )
}
